import type { DiscountByItemId } from '@wix/restaurants-normalizers';
import type { PopulatedMenu } from 'root/apiTypes';
import { ItemsToShow, WidgetType } from 'root/components/MenuAddOn/Settings/Tabs/MenusTab/consts';
import { isMarketPriceItem } from 'root/utils/itemPricing';
import { ALL_FEATURED_TYPES } from 'root/utils/featuredType';

const HIGHLIGHTED_FALLBACK_COUNT = 3;

type VisibleMenusOptions = {
  discountInfoByItemId?: DiscountByItemId;
  isOrderingAppInstalled?: boolean;
  itemsToShow?: ItemsToShow;
  selectedItemIds?: string[];
  selectedMenuId?: string;
  selectedRibbon?: string;
  selectedSectionId?: string;
  widgetType?: WidgetType;
};

const filterMenuToHighlightedItems = (menu: PopulatedMenu, ribbon?: string): PopulatedMenu => {
  const filteredSections = menu.sections
    .map((section) => ({
      ...section,
      items: (section.items ?? []).filter(
        (item) => item.featured && (!ribbon || ribbon === ALL_FEATURED_TYPES || item.featuredInfo?.type === ribbon)
      ),
    }))
    .filter((section) => (section.items?.length ?? 0) > 0);

  if (filteredSections.length > 0) {
    return { ...menu, sections: filteredSections };
  }

  // Fallback: no highlighted items found - show the first N items so the widget is never blank.
  const firstItems = menu.sections.flatMap((section) => section.items ?? []).slice(0, HIGHLIGHTED_FALLBACK_COUNT);
  const firstSection = menu.sections.find((section) => (section.items?.length ?? 0) > 0);

  return { ...menu, sections: firstSection ? [{ ...firstSection, items: firstItems }] : [] };
};

const filterMenuToDiscountItems = (menu: PopulatedMenu, discountInfoByItemId?: DiscountByItemId): PopulatedMenu => ({
  ...menu,
  sections: menu.sections
    .map((section) => ({
      ...section,
      items: (section.items ?? []).filter((item) => !!item.id && !!discountInfoByItemId?.[item.id]),
    }))
    .filter((section) => (section.items?.length ?? 0) > 0),
});

const filterMenuToManualSelection = (menu: PopulatedMenu, selectedItemIds: string[] = []): PopulatedMenu => {
  const orderByItemId = new Map(selectedItemIds.map((id, index) => [id, index]));
  const selectedIds = new Set(selectedItemIds);
  const orderedItems = menu.sections
    .flatMap((section) => section.items ?? [])
    .filter((item) => item.id && selectedIds.has(item.id))
    .sort((a, b) => orderByItemId.get(a.id!)! - orderByItemId.get(b.id!)!);
  const firstSection = menu.sections.find((section) => (section.items?.length ?? 0) > 0);

  return {
    ...menu,
    sections: firstSection ? [{ ...firstSection, items: orderedItems }] : [],
  };
};

const filterMenuToSingleSection = (menu: PopulatedMenu, sectionId?: string): PopulatedMenu => {
  const targetSection = sectionId ? menu.sections.find((section) => section.id === sectionId) : menu.sections[0];

  return { ...menu, sections: targetSection ? [targetSection] : [] };
};

const filterMenuToOrderableItems = (menu: PopulatedMenu): PopulatedMenu => ({
  ...menu,
  sections: menu.sections
    .map((section) => ({
      ...section,
      items: (section.items ?? []).filter((item) => !isMarketPriceItem(item)),
    }))
    .filter((section) => (section.items?.length ?? 0) > 0),
});

export const isMenuAddOnStoredSelectionValid = (menus: PopulatedMenu[], selectedMenuId?: string) =>
  !!selectedMenuId && menus.some((menu) => menu.id === selectedMenuId);

export const getMenuAddOnEffectiveSelectedMenuId = (menus: PopulatedMenu[], selectedMenuId?: string) =>
  isMenuAddOnStoredSelectionValid(menus, selectedMenuId) ? selectedMenuId : menus[0]?.id;

export const getMenuAddOnSelectedMenus = (menus: PopulatedMenu[], selectedMenuId?: string) => {
  const effectiveSelectedMenuId = getMenuAddOnEffectiveSelectedMenuId(menus, selectedMenuId);

  return effectiveSelectedMenuId ? menus.filter((menu) => menu.id === effectiveSelectedMenuId) : menus;
};

export const getMenuAddOnVisibleMenus = (menus: PopulatedMenu[], options: VisibleMenusOptions): PopulatedMenu[] => {
  const selectedMenus = getMenuAddOnSelectedMenus(menus, options.selectedMenuId);
  let filteredMenus = selectedMenus;

  if (options.itemsToShow === ItemsToShow.Highlighted) {
    filteredMenus = selectedMenus.map((menu) => filterMenuToHighlightedItems(menu, options.selectedRibbon));
  } else if (options.itemsToShow === ItemsToShow.Discount) {
    filteredMenus = selectedMenus
      .map((menu) => filterMenuToDiscountItems(menu, options.discountInfoByItemId))
      .filter((menu) => menu.sections.some((section) => (section.items?.length ?? 0) > 0));
  } else if (options.itemsToShow === ItemsToShow.Manual) {
    filteredMenus = selectedMenus
      .map((menu) => filterMenuToManualSelection(menu, options.selectedItemIds))
      .filter((menu) => menu.sections.some((section) => (section.items?.length ?? 0) > 0));
  } else if (options.itemsToShow === ItemsToShow.SingleSection) {
    filteredMenus = selectedMenus
      .map((menu) => filterMenuToSingleSection(menu, options.selectedSectionId))
      .filter((menu) => menu.sections.some((section) => (section.items?.length ?? 0) > 0));
  }

  if (options.widgetType === WidgetType.Ordering && options.isOrderingAppInstalled === false) {
    return [];
  }

  if (options.widgetType === WidgetType.Ordering) {
    return filteredMenus
      .map((menu) => filterMenuToOrderableItems(menu))
      .filter((menu) => menu.sections.some((section) => (section.items?.length ?? 0) > 0));
  }

  return filteredMenus;
};
